import type { LegendListRef } from '@legendapp/list/react-native';
import { type RefObject, useState } from 'react';
import type { ScrollView } from 'react-native';

type UsePaginatedListProps = {
  limit: number;
  indeterminate?: boolean;
  scrollToStartOnPageChange?: boolean;
} & {
  listRef?: RefObject<LegendListRef | null>;
  scrollViewRef?: RefObject<ScrollView | null>;
};

export default function usePaginatedList({
  limit,
  listRef,
  scrollViewRef,
  indeterminate = false,
  scrollToStartOnPageChange = true,
}: UsePaginatedListProps) {
  const [offset, setOffset] = useState(0);

  const scrollToStart = scrollToStartOnPageChange
    ? () => {
        const list = listRef?.current;

        if (list) {
          list.scrollToIndex({ index: 0, animated: true });

          return;
        }

        scrollViewRef?.current?.scrollTo({ y: 0, animated: true });
      }
    : null;

  const scrollToFirstItem = () => {
    if (scrollToStartOnPageChange) {
      setTimeout(() => {
        scrollToStart?.();
      }, 0);
    }
  };

  const nextPage = async () => {
    const nextOffset = offset + limit;
    setOffset(nextOffset);
    scrollToFirstItem();
  };

  const previousPage = async () => {
    const nextOffset = offset - limit;
    setOffset(nextOffset);
    scrollToFirstItem();
  };

  const resetPage = () => {
    setOffset(0);
    scrollToFirstItem();
  };

  return {
    limit: limit + (indeterminate ? 1 : 0),
    offset,
    previousPage,
    nextPage,
    resetPage,
  };
}
